import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { Button, SafeHtmlContent, Text } from '@yes.technology/react-toolkit'

import Step from './components/Step'
import InteractionData from './components/InteractionData'
import GoToTop from './components/GoToTop/GoToTop'
import { useReviewSaveButtonContext } from './components/ReviewSaveButton/ReviewSaveButtonContext'
import { FlatInteractionModelData, InteractionModel } from 'types/shared'
import { ReviewContextProvider } from './context'
import { useAppSelector } from 'state/store'

const useToggleStickySave = () => {
  const staticSaveRef = useRef<HTMLDivElement>(null)
  const { setStickySaveVisible } = useReviewSaveButtonContext()

  useEffect(() => {
    if (!staticSaveRef.current) {
      return
    }

    // show sticky save button if static save button starts to be invisible
    const staticSaveObserver = new IntersectionObserver(
      (entries) => {
        const isFullyVisible = entries[0].intersectionRatio === 1
        setStickySaveVisible(!isFullyVisible)
      },
      {
        threshold: [0, 1],
        rootMargin: '-60px 0px 0px 0px'
      }
    )

    const staticSaveButton = staticSaveRef.current
    staticSaveObserver.observe(staticSaveButton)

    return () => {
      staticSaveObserver.unobserve(staticSaveButton)
    }
  }, [setStickySaveVisible])

  return {
    staticSaveRef
  }
}

interface ReviewProps {
  onSave?: () => void
  goToStep?: (stepUuid: string) => void
  flatData: FlatInteractionModelData
  userName: string
  dateStartInteraction: string
  geolocation: InteractionModel['geolocation']
  reviewActivity?: boolean
  displayedActivityModels?: string[]
  displayMetadata?: boolean
}

const Review = ({
  onSave,
  goToStep,
  flatData,
  userName,
  dateStartInteraction,
  geolocation,
  reviewActivity,
  displayedActivityModels,
  displayMetadata = true
}: ReviewProps) => {
  const { t } = useTranslation()

  const [currentCoords, setCurrentCoords] = useState<GeolocationCoordinates>()

  const { staticSaveRef } = useToggleStickySave()

  const reviewContextValue = {
    flatData,
    user: userName,
    dateStartInteraction,
    geolocation,
    reviewActivity,
    displayedActivityModels
  }

  const reduxDisplayedSteps = useAppSelector(
    (state) => state.steps.displayedSteps
  )

  const { review } = flatData

  const displayedSteps = reduxDisplayedSteps.filter(
    (step) => !step.hide_in_review
  )

  return (
    <ReviewContextProvider value={reviewContextValue}>
      <div className='position-relative pb-5'>
        {displayMetadata && (
          <>
            {!reviewActivity && (
              <div ref={staticSaveRef}>
                <Button
                  className='mt-3'
                  onClick={onSave}
                  impactClass='situationChange'
                >
                  {review?.button_label || t('step.review.submit_button')}
                </Button>
              </div>
            )}

            <div className='mt-2' style={{ textAlign: 'justify' }}>
              <Text variant='label-required-04' as='h3'>
                {review?.des || t('step.review.des')}
              </Text>
              <Text variant='content-04'>
                <SafeHtmlContent
                  html={review?.complement || t('step.review.complement')}
                />
              </Text>
            </div>

            {!review?.hide_basic_data && (
              <InteractionData
                coords={currentCoords}
                onCoordsChange={setCurrentCoords}
                dateStartInteraction={dateStartInteraction}
                userName={userName}
              />
            )}
          </>
        )}

        <Step steps={displayedSteps} goToStep={goToStep} />

        <GoToTop />
      </div>
    </ReviewContextProvider>
  )
}

export default Review
